import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { useQuery } from '@tanstack/react-query';
import * as Updates from 'expo-updates';
import { useEffect } from 'react';

import { getAppConfigV2QueryOptions } from '@/services/getAppConfigV2';
import { useUpdateBannerStore } from '@/services/stores/updateBannerStore';

/**
 * Drives update banner visibility and dismiss state across the Account-tab
 * badge and UpdateReadyBanner. Only active when OTA mode is 'prompt' — in
 * 'auto' mode Expo applies updates natively with no user interaction needed.
 * Resets dismiss when isUpdatePending goes false so a new bundle can
 * re-show both surfaces.
 */
export function useUpdateBanner() {
  const { isUpdatePending } = Updates.useUpdates();

  const { data: appConfig } = useQuery({ ...getAppConfigV2QueryOptions(), enabled: !isWeb });

  const isOtaPromptMode = (appConfig?.otaUpdate?.mode ?? 'auto') === 'prompt';

  const isDismissed = useUpdateBannerStore((state) => state.isDismissed);

  const dismiss = useUpdateBannerStore((state) => state.dismiss);

  const reset = useUpdateBannerStore((state) => state.reset);

  useEffect(() => {
    if (!isUpdatePending && isDismissed) {
      reset();
    }
  }, [isUpdatePending, isDismissed, reset]);

  return {
    isUpdatePending,
    isDismissed,
    isOtaPromptMode,
    showUpdateBadge: isUpdatePending && !isDismissed && isOtaPromptMode,
    dismiss,
  };
}
